import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@/components/Icon";
import BrandMark from "@/components/checkout/BrandMark";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SheetBody, SheetFooter } from "../ui/SheetHost";
import CashierTabs from "../ui/CashierTabs";

/* RedeemSheet (design 5b) — Redeem PCash. Amount is the hero, method is a radio
   list, rules sit by the CTA. REAL money-path (no rewrite):
     • available   → user.cash
     • limits      → $50 min / $1,000 max per request (GET /me/limits informs copy)
     • verified    → GET /kyc/verification .can_redeem (gate the CTA honestly)
     • submit      → POST /wallet/withdraw { amount, payout_method, payout_detail }
   METHODS = only the rails the backend actually supports for payouts (the same
   set the desktop Wallet offers). Brand marks render only where a REAL asset
   exists in /public/brands (PayPal/Venmo/BTC); Bank + USDT use neutral glyphs —
   NO dashed "replace at build" placeholders are shipped.
   Export shape: ({ close }). */

const GREEN = "#00D632";
const MIN = 50;
const MAX = 1000;

// Supported payout rails (match desktop Wallet.jsx `payout_method` options). Each
// carries how it's represented: a real brand mark (asset present) or a glyph icon.
// value = the exact string the backend expects in POST /wallet/withdraw.
const METHODS = [
  { value: "PayPal", label: "PayPal", brand: "paypal", detailLabel: "PayPal email", placeholder: "you@example.com" },
  { value: "Venmo", label: "Venmo", brand: "venmo", detailLabel: "Venmo @username", placeholder: "@username" },
  { value: "Bank transfer (ACH)", label: "Bank (ACH)", icon: "bank", detailLabel: "Account & routing", placeholder: "Account & routing number" },
  { value: "Crypto (BTC)", label: "Bitcoin", brand: "bitcoin", detailLabel: "BTC wallet address", placeholder: "bc1…" },
  { value: "Crypto (USDT)", label: "Tether (USDT)", brand: "tether", detailLabel: "USDT wallet address", placeholder: "0x…" },
];

export default function RedeemSheet({ close, openCashier }) {
  const { user, refresh, streamerMode } = useAuth();
  const [amt, setAmt] = useState("");
  const [method, setMethod] = useState(METHODS[0].value);
  const [detail, setDetail] = useState("");
  const [saveDefault, setSaveDefault] = useState(false);
  const [verif, setVerif] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null); // { amount, reference, method }

  const available = user?.cash ?? 0;
  const mask = !!streamerMode;

  useEffect(() => {
    api.get("/kyc/verification").then(({ data }) => setVerif(data)).catch(() => {});
    api.get("/wallet").then(({ data }) => {
      if (data?.saved_payout_method && METHODS.some((m) => m.value === data.saved_payout_method)) setMethod(data.saved_payout_method);
      if (data?.saved_payout_detail) setDetail(data.saved_payout_detail);
    }).catch(() => {});
  }, []);

  const verified = verif ? !!verif.can_redeem : (user?.kyc_status === "approved");
  const amtNum = Number(amt) || 0;
  const sel = METHODS.find((m) => m.value === method) || METHODS[0];

  const amountError = useMemo(() => {
    if (!amt) return "";
    if (amtNum < MIN) return `Minimum redemption is $${MIN}`;
    if (amtNum > MAX) return `Maximum is $${MAX.toLocaleString()} per request`;
    if (amtNum > available) return "More than your available balance";
    return "";
  }, [amt, amtNum, available]);

  const canSubmit = verified && amtNum >= MIN && amtNum <= MAX && amtNum <= available && detail.trim().length > 0 && !busy;

  const setAmount = (s) => { setErr(""); setAmt(s); };
  const quick = (v) => setAmount(v === "max" ? String(Math.min(available, MAX)) : String(v));

  const submit = async () => {
    setErr("");
    if (amtNum < MIN) { setErr(`Minimum redemption is $${MIN}`); return; }
    if (amtNum > MAX) { setErr(`Maximum is $${MAX.toLocaleString()} per request`); return; }
    if (amtNum > available) { setErr("That's more than your available balance."); return; }
    if (!detail.trim()) { setErr("Enter your payout details."); return; }
    setBusy(true);
    try {
      // The backend enforces the 18+/age gate server-side; this sheet doesn't
      // collect a DOB (the account's verified DOB applies). If an un-aged account
      // hits the gate, the error surfaces below with a clear reason.
      const { data } = await api.post("/wallet/withdraw", {
        amount: amtNum, payout_method: method, payout_detail: detail.trim(), save_default: saveDefault,
      });
      setDone({ amount: amtNum, reference: data?.payout_reference || data?.id || null, method });
      await refresh();
    } catch (e) {
      setErr(apiError(e?.response?.data?.detail) || "Could not submit your redemption.");
    } finally { setBusy(false); }
  };

  // ---- success state ----
  if (done) {
    return (
      <>
        <SheetBody style={{ alignItems: "center", textAlign: "center", display: "flex", flexDirection: "column" }}>
          <span style={{ width: 64, height: 64, marginTop: 10, borderRadius: "50%", background: GREEN, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="check" size={28} style={{ color: "#0A0A0A" }} />
          </span>
          <div style={{ marginTop: 14, font: "800 24px 'Manrope'", letterSpacing: "-.03em", color: "#fff" }}>Redemption requested</div>
          <div className="m-tnum" style={{ marginTop: 6, font: "800 36px 'Manrope'", letterSpacing: "-.04em", color: "#fff" }}>${done.amount.toFixed(2)}</div>
          <div style={{ marginTop: 6, font: "500 13px 'Outfit'", color: "#a1a1aa" }}>to {done.method}</div>
          {done.reference && <div style={{ marginTop: 4, font: "500 12px 'Outfit'", color: "#71717a" }}>Ref · {String(done.reference).slice(-8).toUpperCase()}</div>}
          <p style={{ marginTop: 14, font: "500 12px/1.6 'Outfit'", color: "#71717a" }}>
            Our team reviews prize redemptions and sends them to your chosen method. Processing time TBD. You can track status in your Wallet activity.
          </p>
        </SheetBody>
        <SheetFooter>
          <button type="button" onClick={close} style={ctaSolid}>Done</button>
        </SheetFooter>
      </>
    );
  }

  return (
    <>
      <CashierTabs active="redeem" close={close} openCashier={openCashier} />
      <SheetBody>
        {/* amount hero */}
        <div style={{ textAlign: "center", paddingTop: 2 }}>
          <div style={{ font: "600 12px 'Outfit'", color: "#a1a1aa" }}>Redeem PCash</div>
          <div className="m-tnum" style={{ marginTop: 4, font: "800 46px 'Manrope'", letterSpacing: "-.045em", color: amtNum ? "#fff" : "#52525b" }}>
            ${amtNum ? amtNum.toFixed(amt.includes(".") ? Math.min(2, (amt.split(".")[1] || "").length) : 0) : "0"}
          </div>
          <div style={{ marginTop: 4, font: "500 12px 'Outfit'", color: "#71717a" }}>
            Available {mask ? "•••" : `$${Number(available).toFixed(2)}`} · 1 PCash = $1
          </div>
          {amountError && <div style={{ marginTop: 6, font: "600 12px 'Outfit'", color: "#EF4444" }}>{amountError}</div>}
        </div>

        {/* quick amounts */}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          {[["$50", 50], ["$100", 100], ["Max", "max"]].map(([label, v]) => {
            const active = v === "max" ? amtNum === Math.min(available, MAX) && amtNum > 0 : amtNum === v;
            return (
              <button key={label} type="button" onClick={() => quick(v)}
                style={{ flex: 1, height: 40, borderRadius: 12, border: 0, cursor: "pointer", font: `${active ? 800 : 700} 14px 'Manrope'`,
                  background: active ? "#F4F4F5" : "#1C1C21", color: active ? "#0A0A0A" : "#D4D4D8" }}>
                {label}
              </button>
            );
          })}
        </div>

        {/* amount keypad (numeric amount MAY use the custom keypad) */}
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6 }}>
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "back"].map((k) => (
            <button key={k} type="button" aria-label={k === "back" ? "Backspace" : k}
              onClick={() => {
                if (k === "back") return setAmount(amt.slice(0, -1));
                if (k === ".") { if (amt.includes(".")) return; return setAmount((amt === "" ? "0" : amt) + "."); }
                if (amt.includes(".") && (amt.split(".")[1] || "").length >= 2) return;
                if (amt === "0") return setAmount(k);
                setAmount(amt + k);
              }}
              style={{ height: 46, borderRadius: 12, background: "#1C1C21", color: "#fff", border: 0, cursor: "pointer", font: "800 20px 'Manrope'", boxShadow: "inset 0 1px 0 rgba(255,255,255,.06),0 1px 0 rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {k === "back" ? <Icon name="backspace" size={22} className="text-white" /> : k}
            </button>
          ))}
        </div>

        {/* method radio list */}
        <div style={{ font: "700 11px 'Outfit'", letterSpacing: ".16em", color: "#71717a", margin: "18px 0 8px" }}>SEND TO</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {METHODS.map((m) => {
            const on = method === m.value;
            return (
              <button key={m.value} type="button" role="radio" aria-checked={on} onClick={() => setMethod(m.value)}
                style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 52, padding: "0 14px", borderRadius: 14, border: 0, cursor: "pointer", width: "100%", textAlign: "left",
                  background: on ? "rgba(0,214,50,.07)" : "#141417",
                  boxShadow: on ? "inset 0 0 0 1.5px #00D632" : "inset 0 0 0 1px #26262B" }}>
                <span style={{ width: 30, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                  {m.brand ? <BrandMark brand={m.brand} alt={m.label} height={18} /> : <Icon name={m.icon} size={20} style={{ color: "#a1a1aa" }} />}
                </span>
                <span style={{ flex: 1, font: "600 15px 'Outfit'", color: "#fff" }}>{m.label}</span>
                <span style={on
                  ? { width: 20, height: 20, flex: "none", borderRadius: "50%", boxSizing: "border-box", border: "7px solid #00D632" }
                  : { width: 20, height: 20, flex: "none", borderRadius: "50%", boxSizing: "border-box", border: "2px solid #3F3F46" }} />
              </button>
            );
          })}
        </div>

        {/* destination detail — normal OS keyboard + autofill (NOT the keypad) */}
        <label style={{ display: "block", font: "700 11px 'Outfit'", letterSpacing: ".1em", color: "#71717a", margin: "14px 0 6px" }} htmlFor="m-redeem-detail">
          {sel.detailLabel.toUpperCase()}
        </label>
        <input id="m-redeem-detail" value={detail} onChange={(e) => setDetail(e.target.value)}
          placeholder={sel.placeholder} autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
          inputMode={sel.value === "PayPal" ? "email" : "text"}
          style={{ width: "100%", height: 48, boxSizing: "border-box", padding: "0 14px", borderRadius: 14, background: "#0E0E11", color: "#fff", font: "600 15px 'Outfit'", border: 0, outline: "none", boxShadow: "inset 0 0 0 1px #26262B,inset 0 1px 2px rgba(0,0,0,.45)" }} />

        <label style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: GREEN }} />
          <span style={{ font: "500 13px 'Outfit'", color: "#a1a1aa" }}>Save as my default method</span>
        </label>

        {!verified && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 14, padding: "12px 14px", borderRadius: 14, background: "#141417", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.35)" }}>
            <Icon name="id-card" size={18} style={{ color: "#EF4444", flex: "none", marginTop: 1 }} />
            <span style={{ font: "500 12.5px/1.5 'Outfit'", color: "#d4d4d8" }}>Verify your identity to redeem. Start it from Account → Verification.</span>
          </div>
        )}
        {err && <div style={{ marginTop: 12, font: "600 13px 'Outfit'", color: "#EF4444" }}>{err}</div>}
      </SheetBody>

      <SheetFooter>
        <button type="button" onClick={submit} disabled={!canSubmit}
          style={{ ...ctaSolid, opacity: canSubmit ? 1 : 0.4, cursor: canSubmit ? "pointer" : "default" }}>
          {busy ? "Submitting…" : amtNum >= MIN ? `Redeem $${amtNum.toFixed(2)} to ${sel.label}` : "Redeem PCash"}
        </button>
        <p style={{ font: "500 11px/1.5 'Outfit'", color: "#71717a", marginTop: 10, textAlign: "center" }}>
          $50 minimum · identity verified · not available in excluded states.
        </p>
      </SheetFooter>
    </>
  );
}

const ctaSolid = {
  width: "100%", height: 56, borderRadius: 16, background: "#F4F4F5", color: "#0A0A0A",
  font: "800 16px 'Manrope'", border: 0, cursor: "pointer",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
};
